import React from 'react';
import { AuthForm } from './components/AuthForm';
import { JournalEntry } from './components/JournalEntry';
import { JournalCalendar } from './components/JournalCalendar';
import { VideoLibrary } from './components/VideoLibrary';
import { InstructorLibrary } from './components/InstructorLibrary';
import { DietAnalysis } from './components/DietAnalysis';
import { UserPanel } from './components/UserPanel';
import { PageLoader } from './components/PageLoader';
import { ErrorPage } from './components/ErrorPage';
import { breathingVideos, meditationVideos, stretchVideos, healthVideos } from './data/videos';
import { instructors } from './data/instructors';
import { Wind, Flower2, Heart, Book, Users, Crown, Star, Users2, Video, FlowerIcon, Apple, X } from 'lucide-react';
import { useAppStore } from './lib/store';

export default function App() {
  const {
    session,
    userProfile,
    entries,
    currentEntry,
    isLoading,
    error,
    initialize,
    setSession,
    fetchEntries,
    fetchCurrentEntry,
    reset
  } = useAppStore();

  const [activeSection, setActiveSection] = React.useState('journal');
  const [showPremiumModal, setShowPremiumModal] = React.useState(false);
  const [expandedPremiumModal, setExpandedPremiumModal] = React.useState(true);
  const [selectedDate, setSelectedDate] = React.useState(new Date());

  React.useEffect(() => {
    initialize();
  }, []);

  React.useEffect(() => {
    if (session?.user?.id) {
      fetchCurrentEntry(session.user.id, selectedDate);
    }
  }, [selectedDate, session]);

  const handleSignOut = async () => {
    try {
      reset();
    } catch (error) {
      console.error('Error during sign out:', error);
    }
  };

  const checkPremiumAccess = () => {
    if (userProfile?.subscription_tier === 'premium') return true;
    setShowPremiumModal(true);
    return false;
  };

  const navItems = [
    { name: 'Journal', icon: Book, section: 'journal' },
    { name: 'Breath', icon: Wind, section: 'breath' },
    { name: 'Meditation', icon: Flower2, section: 'meditation' },
    { name: 'Health', icon: Heart, section: 'health' },
    { name: 'Yoga', icon: FlowerIcon, section: 'stretch' },
    { name: 'Diet', icon: Apple, section: 'diet' },
    { name: 'Instructors', icon: Users, section: 'instructors' },
  ];

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <ErrorPage 
        message={error} 
        onRetry={() => {
          initialize();
        }} 
      />
    );
  }

  if (!session) {
    return <AuthForm onAuthSuccess={setSession} />;
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'diet':
        return session?.user?.id ? (
          <DietAnalysis userId={session.user.id} />
        ) : null;
      case 'health':
        return (
          <VideoLibrary
            title="Health & Wellness"
            description="Learn about holistic health practices and vagus nerve exercises"
            videos={healthVideos}
            onPremiumContent={checkPremiumAccess}
          />
        );
      case 'stretch':
        return (
          <VideoLibrary
            title="Yoga Practice"
            description="Improve flexibility and inner peace with guided yoga sessions"
            videos={stretchVideos}
            onPremiumContent={checkPremiumAccess}
          />
        );
      case 'instructors':
        return <InstructorLibrary instructors={instructors} onPremiumContent={checkPremiumAccess} />;
      case 'breath':
        return (
          <VideoLibrary
            title="Breathing Exercises"
            description="Discover powerful breathing techniques to reduce stress, increase energy, and improve focus."
            videos={breathingVideos}
            onPremiumContent={checkPremiumAccess}
          />
        );
      case 'meditation':
        return (
          <VideoLibrary
            title="Meditation Practices"
            description="Explore guided meditations to cultivate mindfulness, peace, and emotional balance."
            videos={meditationVideos}
            onPremiumContent={checkPremiumAccess}
          />
        );
      default:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <JournalEntry
                date={selectedDate}
                entry={currentEntry}
                onSave={() => {
                  if (session?.user?.id) {
                    fetchEntries(session.user.id);
                    fetchCurrentEntry(session.user.id, selectedDate);
                  }
                }}
                onDelete={() => {
                  if (session?.user?.id) {
                    fetchEntries(session.user.id);
                  }
                }}
                onPremiumContent={checkPremiumAccess}
              />
            </div>
            <div className="relative">
              <JournalCalendar
                entries={entries}
                selectedDate={selectedDate}
                onDateSelect={setSelectedDate}
              />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-custom-orange-50 via-custom-orange-100 to-custom-orange-200">
      <div className="flex">
        <nav className="fixed left-0 top-0 h-screen w-20 bg-white shadow-lg flex flex-col items-center py-8 z-50">
          <div className="w-12 h-12 mb-8">
            <img 
              src="/src/2.png" 
              alt="Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          
          <div className="flex-1 flex flex-col gap-4">
            {navItems.map((item) => (
              <button
                key={item.section}
                onClick={() => setActiveSection(item.section)}
                className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all group relative ${
                  activeSection === item.section
                    ? 'bg-gradient-to-r from-custom-orange-400 to-custom-orange-500 text-white shadow-lg'
                    : 'text-gray-400 hover:bg-custom-orange-50'
                }`}
              >
                <item.icon 
                  size={24} 
                  className={`transition-transform ${
                    activeSection === item.section ? 'scale-110' : 'group-hover:scale-110'
                  }`}
                />
                <span className="absolute left-full ml-2 px-2 py-1 bg-white rounded-lg shadow-lg text-sm whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity">
                  {item.name}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-auto flex flex-col gap-4">
            <button
              onClick={() => {
                setShowPremiumModal(true);
                setExpandedPremiumModal(true);
              }}
              className="w-12 h-12 rounded-2xl bg-gradient-to-r from-yellow-400 to-orange-400 text-white flex items-center justify-center hover:from-yellow-500 hover:to-orange-500 transition-all shadow-lg group relative"
            >
              <Crown size={24} />
              <span className="absolute left-full ml-2 px-2 py-1 bg-white rounded-lg shadow-lg text-sm whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity">
                Join Premium
              </span>
            </button>
            <UserPanel session={session} onSignOut={handleSignOut} />
          </div>
        </nav>

        <main className="flex-1 ml-20 p-8">
          <div className="max-w-7xl mx-auto">
            {renderContent()}
          </div>
        </main>
      </div>

      {showPremiumModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-3xl p-6 mx-4 relative max-w-md w-full">
            <button
              onClick={() => {
                setShowPremiumModal(false);
                setExpandedPremiumModal(true);
              }}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white text-gray-500 hover:text-gray-700 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-10"
            >
              <X size={18} />
            </button>

            <div className="text-center">
              <h2 className="text-xl font-bold mb-2">Get Access to All Premium Courses</h2>
              <p className="text-gray-600 text-sm mb-6">
                Join the Shikoba team and unlock exclusive content
              </p>
              
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-4 rounded-xl">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Star className="w-5 h-5 text-yellow-500" />
                    <h3 className="text-lg font-semibold">Premium Courses</h3>
                  </div>
                  <ul className="text-left space-y-2 text-sm mb-4">
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                      <span>Advanced meditation techniques</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                      <span>Exclusive breathing exercises</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
                      <span>Professional wellness programs</span>
                    </li>
                  </ul>
                  <div className="flex gap-3">
                    <button className="flex-1 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white py-2 rounded-lg text-sm font-semibold hover:from-yellow-500 hover:to-yellow-600 transition-all shadow-md">
                      Monthly $9.99
                    </button>
                    <button className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-2 rounded-lg text-sm font-semibold hover:from-yellow-600 hover:to-orange-600 transition-all shadow-md">
                      Yearly $99.99
                    </button>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 p-4 rounded-xl">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Users2 className="w-5 h-5 text-orange-500" />
                    <h3 className="text-lg font-semibold">Team Benefits</h3>
                  </div>
                  <div className="aspect-video rounded-lg overflow-hidden mb-3">
                    <video
                      className="w-full h-full object-cover"
                      controls
                    >
                      <source src="/src/1.mp4" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <ul className="text-left space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <Video className="w-3.5 h-3.5 text-orange-500" />
                      <span>Weekly live coaching sessions</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Video className="w-3.5 h-3.5 text-orange-500" />
                      <span>Exclusive workshop recordings</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Video className="w-3.5 h-3.5 text-orange-500" />
                      <span>Expert technique tutorials</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}